import { GOOGLE_CLIENT_ID, GOOGLE_DRIVE_FILE_SCOPE } from "./google-config";
import {
  GIS_SCRIPT_SRC,
  googleSdk,
  loadGoogleScript,
} from "./load-google-script";

type TokenResponse = {
  access_token?: string;
  expires_in?: number | string;
  error?: string;
};

// One cache for every Google entry point (Sheets, Drive, Slides export) so the user consents
// once per session.
let cachedToken: string | null = null;
let cachedTokenExpiry = 0;
// Concurrent callers must share one request, or Google shows one consent
// popup per caller.
let inFlightToken: Promise<string> | null = null;

export const invalidateGoogleAccessToken = () => {
  cachedToken = null;
  cachedTokenExpiry = 0;
};

export const getGoogleAccessToken = async (): Promise<string> => {
  if (!GOOGLE_CLIENT_ID)
    throw new Error("Google is not configured (VITE_GOOGLE_CLIENT_ID missing)");
  if (cachedToken && Date.now() < cachedTokenExpiry) return cachedToken;
  if (inFlightToken) return inFlightToken;

  inFlightToken = (async () => {
    await loadGoogleScript(
      GIS_SCRIPT_SRC,
      () => !!googleSdk()?.accounts?.oauth2,
    );
    return new Promise<string>((resolve, reject) => {
      const client = googleSdk().accounts.oauth2.initTokenClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: GOOGLE_DRIVE_FILE_SCOPE,
        callback: (resp: TokenResponse) => {
          if (resp.error || !resp.access_token)
            return reject(new Error(resp.error ?? "access_denied"));
          cachedToken = resp.access_token;
          // a minute early, so an about-to-expire token is never reused
          cachedTokenExpiry =
            Date.now() + (Number(resp.expires_in) || 3600) * 1000 - 60_000;
          resolve(resp.access_token);
        },
        // closed or blocked popup; without this the in-flight promise never settles
        error_callback: (err: { type?: string }) =>
          reject(new Error(err?.type ?? "popup_closed")),
      });
      client.requestAccessToken({ prompt: "" });
    });
  })();

  try {
    return await inFlightToken;
  } finally {
    inFlightToken = null;
  }
};
